import config from '../config';
import { alphaClient, playerHeaders, playerUrl } from './shared/playerTransport';

// The selected-character transport resolves the world locally or through the
// authenticated production world route. Database names never cross this boundary.
const requestWorld = async (playerId, path, { method = 'GET', signal, data, onEvent, timing } = {}) => {
  if (!playerId) throw new Error('Select a Play3 character first.');
  timing?.mark('request_sent');
  const headers = { ...(alphaClient ? playerHeaders() : {}),
    ...(data === undefined ? {} : { 'Content-Type': 'application/json' }),
    ...(onEvent ? { Accept: 'application/x-ndjson' } : {}) };
  const baseUrl = `${config.server.url}:${config.server.webServicesPort}`;
  const url = alphaClient ? playerUrl(`${baseUrl}${path}`)
    : `${baseUrl}/local/worlds/${encodeURIComponent(playerId)}${path}`;
  const response = await fetch(url, {
    method, signal, ...(alphaClient ? { credentials: 'same-origin' } : {}),
    ...(Object.keys(headers).length ? { headers } : {}),
    ...(data === undefined ? {} : { body: JSON.stringify(data) }),
  });
  timing?.mark('response_headers');
  if (onEvent && response.ok && response.headers?.get('content-type')?.includes('application/x-ndjson')) {
    const reader = response.body.getReader(), decoder = new TextDecoder();
    let buffer = '', result, complete = false;
    const consume = line => {
      if (!line.trim()) return;
      const event = JSON.parse(line);
      if (event.type === 'timing') { timing?.mark('server_summary', { server: event.timing }); return; }
      if (event.type === 'update') timing?.mark('update_received');
      if (event.type === 'error') throw new Error(event.error?.message || 'Unable to complete Play3.');
      if (event.type === 'result') { timing?.mark('result_received'); result = event.result; complete = true; }
      else if (event.type !== 'heartbeat') onEvent(event);
    };
    try {
      for (;;) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });
        let newline;
        while (!complete && (newline = buffer.indexOf('\n')) !== -1) { consume(buffer.slice(0, newline)); buffer = buffer.slice(newline + 1); }
        // The result is the protocol's terminal event. Waiting for the socket
        // to close can turn a completed turn into a connection error/retry.
        if (complete) return result;
        if (done) break;
      }
      if (buffer.trim()) consume(buffer);
      if (!complete) throw new Error('The progress connection closed. Resume this turn to retrieve its saved progress.');
      return result;
    } finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
  }
  let result;
  try { result = await response.json(); }
  catch {
    throw new Error('Play3 returned an invalid response. Reload the page and retry.');
  }
  if (!response.ok) throw new Error(result?.error?.message || 'Unable to load Play3.');
  return result;
};

export const requestPlay3 = (playerId, path, options) => requestWorld(playerId, `/play3${path}`, options);
export const readPlay3BeingPortrait = (playerId, beingId, signal, idEntityMedia) => requestWorld(playerId,
  `/being/select-portrait-image?idEntity=${encodeURIComponent(beingId)}${idEntityMedia ? `&idEntityMedia=${encodeURIComponent(idEntityMedia)}` : ''}`, { signal });
export const readPlay3Portrait = (playerId, signal) => readPlay3BeingPortrait(playerId, playerId, signal);
export const readPlay3PlaceImage = (playerId, placeId, signal) => requestWorld(playerId,
  `/place/select-approach-image?idEntity=${encodeURIComponent(placeId)}`, { signal });
